"use client";
import { addMonths } from "date-fns/addMonths";
import { startOfMonth } from "date-fns/startOfMonth";
import { useEffect, useRef } from "react";
const MAX_RETRIES = 20;
const RETRY_DELAY_MS = 50;
function getScrollRoot(el) {
    let p = el;
    while(p){
        const { overflowY, overflow } = getComputedStyle(p);
        const isScrollContainer = 'auto' === overflowY || 'scroll' === overflowY || 'auto' === overflow || 'scroll' === overflow;
        if (isScrollContainer) return p;
        p = p.parentElement;
    }
    return el;
}
function setupMobileMonthScrollObserver(container, onMonthChange, startMonth, lastNotifiedMonthRef, observerRef) {
    const grids = container.querySelectorAll('[role="grid"]');
    if (0 === grids.length) return ()=>{};
    const firstMonth = startMonth ?? startOfMonth(new Date());
    const monthDates = Array.from({
        length: grids.length
    }, (_, i)=>addMonths(firstMonth, i));
    const ratioByIndex = new Map();
    const gridList = Array.from(grids);
    const handleIntersection = (entries)=>{
        entries.forEach((entry)=>{
            const index = gridList.indexOf(entry.target);
            if (index >= 0) ratioByIndex.set(index, entry.intersectionRatio);
        });
        let maxRatio = 0;
        let bestIndex = 0;
        ratioByIndex.forEach((ratio, index)=>{
            if (ratio > maxRatio) {
                maxRatio = ratio;
                bestIndex = index;
            }
        });
        if (maxRatio > 0) {
            const month = monthDates[bestIndex];
            const key = `${month.getFullYear()}-${month.getMonth()}`;
            if (lastNotifiedMonthRef.current !== key) {
                lastNotifiedMonthRef.current = key;
                onMonthChange(month);
            }
        }
    };
    const scrollRoot = getScrollRoot(container);
    const observer = new IntersectionObserver(handleIntersection, {
        root: scrollRoot,
        rootMargin: '0px',
        threshold: [
            0,
            0.25,
            0.5,
            0.75,
            1
        ]
    });
    grids.forEach((el)=>observer.observe(el));
    observerRef.current = observer;
    const firstVisible = monthDates[0];
    if (firstVisible) lastNotifiedMonthRef.current = `${firstVisible.getFullYear()}-${firstVisible.getMonth()}`;
    return ()=>{
        observerRef.current?.disconnect();
        observerRef.current = null;
        lastNotifiedMonthRef.current = null;
    };
}
function useMobileMonthScrollObserver({ containerRef, isMobile, onMonthChange, startMonth, numberOfMonths }) {
    const lastNotifiedMonthRef = useRef(null);
    const observerRef = useRef(null);
    useEffect(()=>{
        if (!isMobile || !onMonthChange) return;
        const container = containerRef.current;
        if (!container) return;
        let attempt = 0;
        let cleanup;
        let timeoutId;
        const trySetup = ()=>{
            if (!container) return;
            const grids = container.querySelectorAll('[role="grid"]');
            if (grids.length > 0) {
                cleanup = setupMobileMonthScrollObserver(container, onMonthChange, startMonth, lastNotifiedMonthRef, observerRef);
                return;
            }
            attempt += 1;
            if (attempt < MAX_RETRIES) timeoutId = setTimeout(trySetup, RETRY_DELAY_MS);
        };
        timeoutId = setTimeout(trySetup, 0);
        return ()=>{
            clearTimeout(timeoutId);
            cleanup?.();
            observerRef.current?.disconnect();
            observerRef.current = null;
            lastNotifiedMonthRef.current = null;
        };
    }, [
        containerRef,
        isMobile,
        onMonthChange,
        startMonth,
        numberOfMonths
    ]);
}
export { useMobileMonthScrollObserver };
